import {engineContext} from './context.js';

(function(global){
'use strict';
const VERSION='0.1.0-alpha';
const VALID_REGIONS=['us','europe','asia','em','china','india','latam'];

function finite(v){return typeof v==='number'&&Number.isFinite(v)}
function clamp(v,a,b){return Math.max(a,Math.min(b,v))}
function normWeight(v){return finite(v)?clamp(v,0,1):null}

function normalizeExposure(x){
  x=x||{};
  const region=String(x.region||'').toLowerCase();
  if(!VALID_REGIONS.includes(region)) return null;
  return Object.freeze({
    region,
    revenueShare:normWeight(x.revenueShare),
    assetShare:normWeight(x.assetShare),
    supplierShare:normWeight(x.supplierShare),
    customerShare:normWeight(x.customerShare),
    verification:['VERIFIED','PARTIAL','UNVERIFIED'].includes(x.verification)?x.verification:'UNVERIFIED',
    source:x.source||null
  });
}

function normalizeCompany(x){
  x=x||{};
  const exposures=Array.isArray(x.exposures)?x.exposures.map(normalizeExposure).filter(Boolean):[];
  return Object.freeze({
    symbol:x.symbol||null,
    name:x.name||null,
    owned:x.owned===true,
    watchlist:x.watchlist===true,
    exposures:Object.freeze(exposures)
  });
}

function exposureStrength(e){
  if(!e) return null;
  const vals=[
    ['revenue',e.revenueShare,.40],
    ['customer',e.customerShare,.25],
    ['supplier',e.supplierShare,.20],
    ['asset',e.assetShare,.15]
  ].filter(x=>finite(x[1]));
  if(!vals.length) return null;
  const w=vals.reduce((a,x)=>a+x[2],0);
  return vals.reduce((a,x)=>a+x[1]*x[2],0)/w;
}

function dominantRegions(company){
  const c=normalizeCompany(company);
  return Object.freeze(
    c.exposures.map(e=>Object.freeze({
      region:e.region,
      strength:exposureStrength(e),
      verification:e.verification
    }))
    .filter(x=>finite(x.strength))
    .sort((a,b)=>b.strength-a.strength)
  );
}

function marketLookup(context){
  const rows=(context&&Array.isArray(context.regions))?context.regions:[];
  return Object.fromEntries(rows.map(r=>[String(r.key||'').toLowerCase(),r]));
}

function impactForCompany(company,marketContext){
  const c=normalizeCompany(company);
  const lookup=marketLookup(marketContext);
  const impacts=[];
  for(const e of c.exposures){
    const strength=exposureStrength(e);
    const m=lookup[e.region];
    if(!finite(strength)||!m) continue;
    const return30d=finite(m.return30d)?m.return30d:null;
    const risk = m.status||m.context||null;
    let pressure='UNKNOWN';
    if(risk==='RISK_ELEVATED') pressure='HIGH';
    else if(risk==='ROTATION_RISK'||risk==='REVIEW') pressure='MODERATE';
    else if(risk==='CONSTRUCTIVE') pressure='LOW';
    impacts.push(Object.freeze({
      region:e.region,
      exposureStrength:strength,
      marketStatus:risk,
      return30d,
      verification:e.verification,
      pressure
    }));
  }
  impacts.sort((a,b)=>b.exposureStrength-a.exposureStrength);

  const high=impacts.filter(i=>i.pressure==='HIGH'&&i.exposureStrength>=.25);
  const moderate=impacts.filter(i=>i.pressure==='MODERATE'&&i.exposureStrength>=.25);

  let level='LOW';
  if(high.length) level='HIGH';
  else if(moderate.length) level='MODERATE';
  else if(!impacts.length) level='UNKNOWN';

  return Object.freeze({
    symbol:c.symbol,
    scope:c.owned?'OWNED':c.watchlist?'WATCHLIST':'RESEARCH',
    level,
    impacts:Object.freeze(impacts),
    topExposure:impacts[0]||null,
    tradeSignal:false,
    companyQualityOverride:false
  });
}

function portfolioExposure(companies,marketContext){
  const list=Array.isArray(companies)?companies.map(normalizeCompany):[];
  const results=list.map(c=>impactForCompany(c,marketContext));
  const owned=results.filter(r=>r.scope==='OWNED');
  const watchlist=results.filter(r=>r.scope==='WATCHLIST');
  const flagged=results.filter(r=>r.level==='HIGH'||r.level==='MODERATE');

  return Object.freeze({
    owned:Object.freeze(owned),
    watchlist:Object.freeze(watchlist),
    flagged:Object.freeze(flagged),
    counts:Object.freeze({
      owned:owned.length,
      watchlist:watchlist.length,
      high:results.filter(r=>r.level==='HIGH').length,
      moderate:results.filter(r=>r.level==='MODERATE').length,
      unknown:results.filter(r=>r.level==='UNKNOWN').length
    })
  });
}

function contradiction(company,marketContext,companyStrength){
  const impact=impactForCompany(company,marketContext);
  const cs=String(companyStrength||'UNKNOWN').toUpperCase();
  if(cs==='STRONG'&&impact.level==='HIGH'){
    return Object.freeze({
      state:'STRONG_COMPANY_HIGH_EXTERNAL_PRESSURE',
      action:'REVIEW_EXPOSURE_AND_TIMING',
      tradeSignal:false
    });
  }
  if(cs==='WEAK'&&impact.level==='LOW'){
    return Object.freeze({
      state:'WEAK_COMPANY_BENIGN_EXTERNAL_CONTEXT',
      action:'REVIEW_COMPANY',
      tradeSignal:false
    });
  }
  return Object.freeze({state:'NO_MAJOR_CONTRADICTION',action:'NO_ACTION',tradeSignal:false});
}

function selfTest(){
  const t=[],ok=(n,p)=>t.push({name:n,pass:!!p});
  const company={
    symbol:'AAA',owned:true,
    exposures:[
      {region:'china',revenueShare:.50,customerShare:.40,verification:'VERIFIED'},
      {region:'us',revenueShare:.20,verification:'VERIFIED'}
    ]
  };
  const market={regions:[
    {key:'china',status:'RISK_ELEVATED',return30d:-8},
    {key:'us',status:'CONSTRUCTIVE',return30d:3}
  ]};
  let e=normalizeExposure({region:'china',revenueShare:1.4});
  ok('exposure is clamped',e.revenueShare===1);
  ok('unknown region rejected',normalizeExposure({region:'moon'})===null);
  ok('dominant region detected',dominantRegions(company)[0].region==='china');
  let i=impactForCompany(company,market);
  ok('high pressure detected on material exposure',i.level==='HIGH');
  ok('owned scope preserved',i.scope==='OWNED');
  ok('trade signal never emitted',i.tradeSignal===false);
  ok('company quality never overridden',i.companyQualityOverride===false);
  ok('top exposure is strongest region',i.topExposure.region==='china');
  const p=portfolioExposure([company,{symbol:'BBB',watchlist:true,exposures:[{region:'us',revenueShare:.8,verification:'VERIFIED'}]}],market);
  ok('portfolio separates owned',p.counts.owned===1);
  ok('portfolio separates watchlist',p.counts.watchlist===1);
  ok('portfolio flags high pressure holding',p.counts.high===1);
  const c=contradiction(company,market,'STRONG');
  ok('strong company plus regional stress creates explicit contradiction',c.state==='STRONG_COMPANY_HIGH_EXTERNAL_PRESSURE');
  ok('contradiction remains review-only',c.tradeSignal===false);

  return Object.freeze({passed:t.filter(x=>x.pass).length,total:t.length,tests:Object.freeze(t)});
}

const API={VERSION,VALID_REGIONS,normalizeExposure,normalizeCompany,exposureStrength,dominantRegions,impactForCompany,portfolioExposure,contradiction,selfTest};
try{API.selfTestResult=selfTest()}catch(e){API.bootstrapError=String(e&&e.stack||e)}
global.ZooInvestGlobalExposureEngine=Object.freeze(API);
})(engineContext);
